import { Alert, Box, Skeleton, Stack } from '@mui/material';

import { useIsPanelVisible } from '~/components/common/tabs/PanelVisibility';
import { PeriodSelect } from '~/components/serviceHistory/PeriodSelect';
import type { PeriodWindow } from '~/components/serviceHistory/periodWindow';
import { periodWindowRange } from '~/components/serviceHistory/periodWindow';
import { VisitsCompletionTable } from '~/components/visitsCompletion/VisitsCompletionTable';
import type { VisitPeriodScopeTarget } from '~/data/hooks/useMaintenanceVisitPeriods';
import { useMaintenanceVisitSummary } from '~/data/hooks/useMaintenanceVisitPeriods';
import { TRANSIENT_SURFACE_RADIUS } from '~/theme';

export function VisitsCompletionTab({
  scope,
  emptyMessage,
  window,
  alwaysShowProperty = false,
  onWindowChange,
}: {
  scope: VisitPeriodScopeTarget;
  emptyMessage: string;
  window: PeriodWindow;
  alwaysShowProperty?: boolean;
  onWindowChange: (next: PeriodWindow) => void;
}) {
  // Nothing is fetched or laid out until this is the panel on screen. The summary runs
  // the whole derivation over every car in scope, which is far too much to spend on a
  // tab most page views never open, and the grid sizes its columns against a container
  // that measures zero while hidden.
  const isVisible = useIsPanelVisible();
  const {
    summaries,
    window: drawnWindow,
    isLoading,
    error,
    refresh,
  } = useMaintenanceVisitSummary(isVisible ? { ...scope, ...periodWindowRange(window) } : null);
  const showingStale = Boolean(isLoading) && summaries.length > 0;

  // Both are shown under the period selector rather than in place of it, so a window
  // that fails or holds nothing is not a dead end.
  const isEmpty = !error && !isLoading && summaries.length === 0;

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <Stack gap={2}>
        <PeriodSelect window={window} onWindowChange={onWindowChange} />
        {error ? <Alert severity="error">Maintenance completion could not be loaded.</Alert> : null}
        {isEmpty ? <Alert severity="info">{emptyMessage}</Alert> : null}
        <Box sx={{ position: 'relative' }}>
          {showingStale ? (
            <Box
              role="status"
              aria-label="Loading visits"
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                p: 2,
                bgcolor: 'background.paper',
              }}
            >
              <Skeleton variant="text" width="25%" height={28} sx={{ bgcolor: 'grey.200' }} />
              {Array.from({ length: 6 }, (_, row) => (
                <Skeleton
                  key={row}
                  variant="rectangular"
                  height={40}
                  sx={{ bgcolor: 'grey.200', borderRadius: `${TRANSIENT_SURFACE_RADIUS}px` }}
                />
              ))}
            </Box>
          ) : null}
          {isVisible && !error && !isEmpty ? (
            <VisitsCompletionTable
              summaries={summaries}
              loading={Boolean(isLoading) && summaries.length === 0}
              range={periodWindowRange(window)}
              window={drawnWindow}
              alwaysShowProperty={alwaysShowProperty}
              onPeriodChanged={() => void refresh()}
            />
          ) : null}
        </Box>
      </Stack>
    </Box>
  );
}
